<template>
	<form
		:class="[
			'styled-form',
			'styled-form--white-box',
			{'is-loading': isLoading}
		]"
		@submit.prevent="onSubmit"
	>

		<div v-if="isLoading" class="styled-form__overlay">
			<div class="preloader"></div>
		</div>

		<global-message v-if="errors.global" :value="errors.global"></global-message>

		<div class="styled-form__header">
			<h2 class="styled-form__title">Update your details</h2>
		</div>

		<div class="o-grid o-grid--no-vgutter">

			<div class="o-grid__item o-1/2@largeMobile">
				<form-input
					v-model="data.firstname"
					name="firstname"
					label="First Name *"
					:error="errors.firstname"
				></form-input>
			</div>

			<div class="o-grid__item o-1/2@largeMobile">
				<form-input
					v-model="data.lastname"
					name="lastname"
					label="Last Name *"
					:error="errors.lastname"
				></form-input>
			</div>

			<div class="o-grid__item o-1/2@largeMobile">
				<styled-date-picker
						v-model="data.birthdate"
						name="birthdate"
						label="Date of Birth *"
						:error="errors.birthdate"
				></styled-date-picker>
			</div>
			
			<div class="o-grid__item o-1/2@largeMobile">
				<form-input
					v-model="data.address1_line1"
					name="address1_line1"
					label="Address line 1"
					:error="errors.address1_line1"
				></form-input>
			</div>

			<div class="o-grid__item o-1/2@largeMobile">
				<form-input
					v-model="data.address1_line2"
					name="address1_line2"
					label="Address line 2"
					:error="errors.address1_line2"
				></form-input>
			</div>

			<div class="o-grid__item o-1/2@largeMobile">
				<form-input
					v-model="data.address1_city"
					name="address1_city"
					label="Suburb"
					:error="errors.address1_city"
				></form-input>
			</div>

			<div class="o-grid__item o-1/2@largeMobile">
				<form-input
            v-if="!showAustralianStates"
            v-model="data.address1_stateorprovince"
            name="address1_stateorprovince"
            label="State"
            :error="errors.address1_stateorprovince"
				></form-input>
        <select-input
            v-else
            v-model="data.address1_stateorprovince"
            name="address1_stateorprovince"
            label="State"
            :error="errors.address1_stateorprovince"
            :options="australianStates"
        ></select-input>
			</div>

			<div class="o-grid__item o-1/2@largeMobile">
				<form-input
					v-model="data.address1_postalcode"
					name="address1_postalcode"
					:label="postcodeLabel"
					:error="errors.address1_postalcode"
          type="string"
          :maxLength="postcodeLength"
					@input="validatePostcodeInline"
					:class="{'is-disabled': isPostcodeDisabled}"
					:disabled="isPostcodeDisabled"
				></form-input>
			</div>

			<div class="o-grid__item o-1/2@largeMobile">
        <select-input
            v-model="data.address1_country"
            name="address1_country"
            label="Country *"
            :error="errors.address1_country"
            :options="countries"
            @change="isAustraliaSelected"
        ></select-input>
			</div>

			<div class="o-grid__item o-1/2@largeMobile">
				<form-input
						v-model="data.mobilephone"
						name="mobilephone"
						label="Mobile"
						:error="errors.mobilephone"
				></form-input>
			</div>

			<div class="o-grid__item">
				<div class="text--center">
					<router-link
						class="btn btn--secondary"
						:disabled="isLoading"
						:to="{name: 'myAccount'}"
					>Cancel</router-link>
					<button class="btn" :disabled="isLoading">Update</button>
				</div>
			</div>

		</div>
	</form>
</template>

<script>
	import moment from 'moment';
	import FormInput from '../formFields/FormInput.vue';
	import StyledDatePicker from '../formFields/StyledDatePicker.vue';
	import GlobalMessage from '../messages/GlobalMessage.vue';
  import SelectInput from "../formFields/SelectInput";
  import CountriesArray from "../../utils/countries"
  import AustralianStatesArray from "../../utils/australianStatesArray";

	export default {
    components: {
      SelectInput,
      FormInput,
      StyledDatePicker,
      GlobalMessage
    },
    props: {
      user: {
        type: Object,
        required: true,
        default: {}
      },
      submit: {
        type: Function,
        required: true
      }
    },
    data() {
      const user = this.user;

      return {
        countries: CountriesArray,
        australianStates: AustralianStatesArray,
        isChanged: false,
        showAustralianStates: user.address1_country === 'AU',
        isLoading: false,
        data: {
          firstname: user.given_name,
          lastname: user.family_name,
          mobilephone: user.mobilephone,
          birthdate: user.birthdate,
          address1_line1: user.address1_line1,
          address1_line2: user.address1_line2,
          address1_city: user.address1_city,
          address1_stateorprovince: user.address1_stateorprovince,
          address1_postalcode: user.address1_postalcode,
          address1_country: user.address1_country,
        },
        locked: {
          firstname: user.given_name,
          lastname: user.family_name
        },
        errors: {},
      }
    },
    computed: {
      checkedUser() {
        if (this.user) {
          return this.user;
        } else {
          return {};
        }
      },
      disabledEdit() {
        return this.checkedUser.identities && this.checkedUser.identities.length;
      },
      postcodeLength() {
        // Australia uses 4-digit postcodes, other countries can have up to 10 characters
        return this.data.address1_country === 'AU' ? 4 : 10;
      },
      postcodeLabel() {
        // Only mark as required for Australia
        return this.data.address1_country === 'AU' ? 'Postcode *' : 'Postcode';
      },
      isPostcodeDisabled() {
        // Disable postcode field for non-Australian countries
        return this.data.address1_country !== 'AU';
      }
    },
    watch: {
      data: {
        handler() {
          this.isChanged = true;
        },
        deep: true
      }
    },
    methods: {
      isAustraliaSelected(country) {
        this.showAustralianStates = country === 'AU';

        if (!this.showAustralianStates) {
          this.data.address1_stateorprovince = '';
        }
      },
      onSubmit() {
        this.errors = this.validate(this.data);
        if (Object.keys(this.errors).length === 0) {
          this.isLoading = true;

          this.submit(this.data).catch(errors => {
            this.isLoading = false;
            // this.errors = errors;
            this.errors = {
              global: 'Something went wrong, please try again.'
            }
          });
        }
      },
      validate(data) {
        const errors = {};

        if (!data.firstname.length) errors.firstname = 'This field is required';
        if (!data.lastname.length) errors.lastname = 'This field is required';

        if (!data.address1_country) {
          errors.address1_country = 'Country field is required';
        }
        
        // Validate postcode based on country
        const postcodeErrors = this.validatePostcode(data.address1_postalcode, data.address1_country);
        if (postcodeErrors.length) {
          errors.address1_postalcode = postcodeErrors;
        }

        let invalidYearsAgo = moment().subtract(115, 'years');
        if (!moment(data.birthdate, 'YYYY-MM-DD').isValid()) {
          errors.birthdate = 'This field is required';
        } else if (moment(data.birthdate, 'YYYY-MM-DD').diff(moment(new Date())) > 0
            || moment(data.birthdate, 'YYYY-MM-DD').diff(invalidYearsAgo, 'years') <= 0) {
          errors.birthdate = 'Enter a valid date of birth';
        }

        return errors;
      },
      validatePostcode(postcode, country) {
        let errors = '';
        
        // If Australia is selected, apply Australian postcode validation
        if (country === 'AU') {
          const australianPostcodeRegex = new RegExp('^(?:(?:[2-8]\\d|9[0-7]|0?[28]|0?9(?=09))(?:\\d{2}))$');
          
          if (!postcode) {
            errors = 'This field is required';
          } else if (!australianPostcodeRegex.test(postcode)) {
            errors = 'Enter a valid Australian postcode';
          }
        } else {
          // For other countries, postcode is not required
          // Only validate length if provided
          if (postcode && postcode.length > 10) {
            errors = 'Postcode is too long';
          }
        }
        
        return errors;
      },
      validatePostcodeInline() {
        if (this.data.address1_postalcode && this.data.address1_postalcode.length > 0) {
          const error = this.validatePostcode(this.data.address1_postalcode, this.data.address1_country);
          if (error) {
            this.errors.address1_postalcode = error;
          } else {
            delete this.errors.address1_postalcode;
          }
        } else {
          delete this.errors.address1_postalcode;
        }
      }
    }
  };
</script>

<style lang="scss" scoped>

</style>
